import { initJsPsych } from "jspsych";
import audioButtonResponse from "@jspsych/plugin-audio-button-response";
import fullscreen from "@jspsych/plugin-fullscreen";
import jsPsychHtmlButtonResponse from '@jspsych/plugin-html-button-response';
import preload from '@jspsych/plugin-preload';

const JsPsychTask = ({ onComplete, display_element, timeL, timelineVariables, setTaskStarted, timeout, panelType, instructionText }) => {

  const jsPsych = initJsPsych({
    on_finish: async () => {

      const filteredData = jsPsych?.data?.get()?.filterCustom((trial) => {
        return trial?.task === 'response';
      });

      const score = ((filteredData?.trials?.filter((item) => item.is_correct == true)?.length / filteredData?.trials?.length) * 100)?.toFixed(2)

      jsPsych.run([
        {
          type: jsPsychHtmlButtonResponse,
          // stimulus: `<h1>The task has been successfully completed</h1><h1>Your Score : ${score}%</h1><p><br></p>`,
          stimulus: `<h1>The task has been successfully completed</h1>`,
          choices: ["Finish"],
          on_finish: () => {
            onComplete(filteredData);
          }
        }
      ]);
    },
    display_element,
  });

  const timeline = [...timeL];
  const audioFiles = timelineVariables?.map(item => item?.audio);

  const practiceVariables = timelineVariables?.filter((item) => item?.trailPart !== "test")?.map((item) => ({
    audio: item?.audio,
    choices: item?.choices,
    correct_choice: parseInt(item?.answer, 10),
  }));

  const normalTestVariables = timelineVariables?.filter((item) => item?.trailPart === "test");

  const sendResponseToServer = async (response) => { };

  const shuffleArray = (array) => {
    return array?.sort(() => Math?.random() - 0.5);
  };

  const practiceTrials = practiceVariables?.flatMap((item) => {
    const shuffledChoices = shuffleArray([...item?.choices]);
    const correctChoiceIndex = shuffledChoices?.indexOf(item?.choices[item?.correct_choice]);

    const trial = {
      type: audioButtonResponse,
      auto_preload: false,
      continue_after_error: false,
      stimulus: item?.audio,
      choices: shuffledChoices,
      // prompt: "<h2>What did the person say?</h2><div id='countdown'></div>",
      prompt: "<h2>What did the person say?</h2>",
      trial_duration: timeout,
      post_trial_gap: 500,
      data: { task: 'practice-response', correct_response: correctChoiceIndex },
      on_load: () => {
        const timeInMillis = typeof timeout === 'number' && !isNaN(timeout) ? timeout : 15000;
        const countdownElement = display_element?.querySelector("#countdown");

        if (countdownElement) {
          let timeLeft = timeInMillis / 1000;
          // countdownElement.innerHTML = `Time left: ${timeLeft}s`;

          const countdownInterval = setInterval(() => {
            timeLeft -= 1;
            if (timeLeft >= 0) {
              // countdownElement.innerHTML = `Time left: ${timeLeft}s`;
            } else {
              clearInterval(countdownInterval);
            }
          }, 1000);
        }
      },
      on_finish: (data) => {
        const correct_choice = data?.correct_response;
        const participant_response = data?.response;
        data.correct = participant_response === correct_choice;
        data.no_response = participant_response === null;
      },
    };

    const feedbackTrial = {
      type: jsPsychHtmlButtonResponse,
      stimulus: function () {
        const lastTrial = jsPsych.data.get().last(1).values()[0];
        const isCorrect = lastTrial.response === lastTrial.correct_response;
        if (lastTrial.response === null) {
          return "<h1>Too Slow</h1>";
        }

        return isCorrect
          ? "<h1 >Correct!</h1>"
          : "<h1 >Incorrect!</h1>";
      },
      choices: [],
      trial_duration: 1000,
    };

    return [trial, feedbackTrial];
  });

  let practiceMistakes = false;

  const checkPracticeMistakes = {
    timeline: practiceTrials,
    loop_function: () => {
      const last_trials = jsPsych?.data?.get().filter({ task: 'practice-response' }).last(practiceVariables?.length || 2);
      const mistakes = last_trials?.filter({ correct: false }).count();
      practiceMistakes = mistakes > 0;
      return false;
    },
  };

  const retryMessage = {
    type: jsPsychHtmlButtonResponse,
    stimulus: `
      <div class="continue-des">
        <figure><img src="/images/close01.png" alt="close"/></figure>
        <figcaption>
          <p>You have too many mistakes on the practice.</p>
          <p>Click "Continue" to repeat the practice.</p>
        </figcaption>
      </div>`,
    choices: ['Continue'],
    button_html: (choice) => `
      <div class="start-task-des">
        <div class="start-btn">
          <button type="button" class="btn">${choice}</button>
        </div>
      </div>`,
  };

  const postPracticeMessage = {
    type: jsPsychHtmlButtonResponse,
    stimulus: `
      <div class="continue-des">
        <figure><img src="/images/i-help.svg" alt="Help" /></figure>
        <figcaption>
         ${instructionText}
        </figcaption>
      </div>`,
    choices: ['Continue'],
    button_html: (choice) => `
      <div class="start-task-des">
        <div class="start-btn">
          <button type="button" class="btn">${choice}</button>
        </div>
      </div>`,
    on_finish: () => {
      practiceMistakes = true;
    },
  };

  timeline.push(checkPracticeMistakes);

  timeline.push({
    conditional_function: () => practiceMistakes,
    timeline: [retryMessage, checkPracticeMistakes],
    loop_function: () => practiceMistakes
  });

  timeline.push({
    conditional_function: () => !practiceMistakes,
    timeline: [postPracticeMessage],
  });

  const practiceAudioFiles = practiceVariables?.map(item => item?.audio).filter(audio => audio);
  const mainAudioFiles = audioFiles?.filter(audio => audio);
  const allAudioFiles = [...practiceAudioFiles, ...mainAudioFiles];

  const preloadAudioFiles = {
    type: preload,
    auto_preload: true,
    audio: allAudioFiles,
    continue_after_error: true,
    on_load: () => console?.log('All audio files preloaded successfully.'),
    on_error: (error) => console?.log('Error preloading audio:', error),
  };

  timeline.unshift(preloadAudioFiles);


  // Main task
  normalTestVariables.forEach((item) => {
    const shuffledChoices = [...item?.choices]?.sort(() => Math?.random() - 0.5);
    const originalChoices = item?.choices;
    const shuffledIndices = shuffledChoices?.map(choice => originalChoices?.indexOf(choice));

    timeline.push({
      type: audioButtonResponse,
      stimulus: item?.audio,
      choices: shuffledChoices,
      prompt: function () {
        const currentIndex = jsPsych.data.get().filter({ task: 'response' }).count() + 1;
        const total = timelineVariables.length;
        const percent = Math.round((currentIndex / total) * 100);

        return `
      <div id="countdown" style="display:none"></div>
    <h2 style="text-align: center; font-size: 18px; font-weight: 500; margin-top: 20px;">
      What did the person say?
    </h2>
          <div style="margin-bottom: 30px; width: 100%; max-width: 600px; margin-left: auto; margin-right: auto;">
            <div style="
              height: 10px;
              background-color: #f3e5dc;
              border-radius: 999px;
              overflow: hidden;
              box-shadow: inset 0 1px 2px rgba(0,0,0,0.1);
            ">
              <div style="
                height: 100%;
                width: ${percent}%;
               background-color:rgb(0, 0, 0);
                transition: width 0.4s ease;
              "></div>
            </div>
            <div style="margin-top: 6px; text-align: right; font-size: 13px; color: #795548; font-weight: 500;">
              ${percent}% completed
            </div>
          </div>
      
         
        `;
      },
      trial_duration: timeout,
      post_trial_gap: 500,
      data: {
        task: 'response',
        id: item?.id,
        courseId: item?.courseId,
        taskId: item?.taskId,
        original_choices: originalChoices,
        shuffled_indices: shuffledIndices,
        options: shuffledChoices,
      },
      on_load: () => {
        const timeInMillis = typeof timeout === 'number' && !isNaN(timeout) ? timeout : 15;

        const countdownElement = display_element?.querySelector("#countdown");
        let timeLeft = timeInMillis / 1000;
        // countdownElement.innerHTML = `Time left: ${timeLeft}s`;

        const countdownInterval = setInterval(() => {
          timeLeft -= 1;
          // countdownElement.innerHTML = `Time left: ${timeLeft}s`;

          if (timeLeft <= 0) {
            clearInterval(countdownInterval);
          }
        }, 1000);
      },
      on_finish: (data) => {
        const participant_response_index = data?.response;
        const responseTime = data.rt;

        // if (participant_response_index === null) { }
        // else {
          const originalIndex = data?.shuffled_indices[participant_response_index];
          setTaskStarted(true);
          sendResponseToServer({
            courseId: data?.courseId,
            taskId: data?.taskId,
            questionId: data?.id,
            answer: originalIndex?.toString() || 'No Response',
            options: data?.options,
            responseTime: responseTime || timeout,
            // roleType: panelType,
          });
        // }
      },
    });
  });

  timeline?.push({
    type: fullscreen,
    fullscreen_mode: false,
    on_finish: () => {
      setTaskStarted(false);
    },
  });

  jsPsych.run(timeline);
};

export default JsPsychTask;